import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button, Space, Alert, Checkbox } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { markingApi } from '../../api/marking';
import { rawToGs1 } from '../../utils/dataMatrix';
import type { UnreceivedItem } from '../../api/unreceived';

// Печать A4 «Продажи без оприходования» — марки к дофискализации в 1С
// (unreceived_sales.md v3). Паттерн — MarkingPrintPreview: скрытая область,
// клонируемая в новое окно с самодостаточным CSS. DataMatrix вставляются
// инлайн-SVG (generateDataMatrixSvgText) — переживают клонирование в окно печати.

export interface UnreceivedPrintItem extends UnreceivedItem {
  order_id: number;
  order_number: string | null;
}

interface Props {
  open: boolean;
  items: UnreceivedPrintItem[];
  onClose: () => void;
}

const printedKey = (markingCode: string) => `unreceived_printed:${markingCode}`;

const PRINT_CSS = `
  @page { size: A4 portrait; margin: 10mm; }
  html, body { margin: 0; padding: 0; background: #fff; font-family: sans-serif; }
  .u-header { font-size: 14pt; font-weight: 700; margin-bottom: 2mm; }
  .u-subheader { font-size: 10pt; color: #333; margin-bottom: 6mm; }
  .u-item { display: flex; gap: 6mm; border: 1px solid #000; padding: 4mm;
            margin-bottom: 4mm; page-break-inside: avoid; break-inside: avoid; }
  .u-dm svg { width: 30mm; height: 30mm; }
  .u-info { flex: 1; font-size: 10pt; line-height: 1.4; }
  .u-name { font-size: 11pt; font-weight: 700; margin-bottom: 1mm; }
  .u-volume { font-size: 16pt; font-weight: 700; margin: 1mm 0; }
  .u-gs1 { font-family: monospace; font-size: 8pt; word-break: break-all; color: #333; }
  .u-order { margin-top: 1mm; color: #333; }
  .u-retake-title { font-size: 12pt; font-weight: 700; margin: 6mm 0 2mm; }
  .u-retake-item { font-size: 10pt; margin-bottom: 1mm; page-break-inside: avoid; }
  .u-footer { margin-top: 6mm; font-size: 10pt; border-top: 1px solid #000; padding-top: 3mm; }
  .no-print { display: none !important; }
`;

const openPrintWindow = (printArea: HTMLElement) => {
  const w = window.open('', '_blank', 'width=900,height=1100');
  if (!w) return;
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Печать</title>
<style>${PRINT_CSS}</style></head><body>${printArea.innerHTML}</body></html>`);
  w.document.close();
  w.onload = () => {
    w.focus();
    w.print();
    setTimeout(() => w.close(), 300);
  };
};

const qtyText = (it: UnreceivedPrintItem) =>
  it.product_type === 'bulk'
    ? `${Number(it.quantity)} л`
    : `${Number(it.quantity)} ${it.unit || 'шт'}`;

const UnreceivedPrintPreview: React.FC<Props> = ({ open, items, onClose }) => {
  const printAreaRef = useRef<HTMLDivElement>(null);
  const [svgMap, setSvgMap] = useState<Record<string, string>>({});
  const [printed, setPrinted] = useState<Record<string, boolean>>({});

  const printable = useMemo(
    () => items.filter((i) => i.has_full_code && i.raw_code),
    [items]
  );
  const noTail = useMemo(
    () => items.filter((i) => !(i.has_full_code && i.raw_code)),
    [items]
  );

  useEffect(() => {
    if (!open) return;
    const initial: Record<string, boolean> = {};
    items.forEach((i) => {
      initial[i.marking_code] = localStorage.getItem(printedKey(i.marking_code)) === '1';
    });
    setPrinted(initial);
    setSvgMap({});
    let cancelled = false;
    Promise.all(
      printable.map(async (i) => {
        try {
          const svg = await markingApi.generateDataMatrixSvgText(i.raw_code as string, 4);
          return [i.marking_code, svg] as const;
        } catch {
          return null;
        }
      })
    ).then((entries) => {
      if (cancelled) return;
      const map: Record<string, string> = {};
      entries.forEach((e) => {
        if (e) map[e[0]] = e[1];
      });
      setSvgMap(map);
    });
    return () => {
      cancelled = true;
    };
  }, [open, printable, items]);

  const togglePrinted = (markingCode: string, checked: boolean) => {
    if (checked) localStorage.setItem(printedKey(markingCode), '1');
    else localStorage.removeItem(printedKey(markingCode));
    setPrinted((p) => ({ ...p, [markingCode]: checked }));
  };

  const handlePrint = () => {
    if (printable.length === 0 || !printAreaRef.current) return;
    openPrintWindow(printAreaRef.current);
  };

  const today = new Date().toLocaleDateString('ru-RU');

  const renderItem = (it: UnreceivedPrintItem, forPrint: boolean) => (
    <div className="u-item" key={it.id} style={forPrint ? undefined : {
      display: 'flex', gap: 16, border: '1px solid #d9d9d9', borderRadius: 8,
      padding: 12, marginBottom: 12,
    }}>
      <div className="u-dm">
        {svgMap[it.marking_code] ? (
          forPrint ? (
            <span dangerouslySetInnerHTML={{ __html: svgMap[it.marking_code] }} />
          ) : (
            <span
              style={{ display: 'inline-block', width: 96, height: 96 }}
              dangerouslySetInnerHTML={{ __html: svgMap[it.marking_code] }}
            />
          )
        ) : (
          <span style={{ fontSize: 12, color: '#888' }}>Загрузка...</span>
        )}
      </div>
      <div className="u-info" style={forPrint ? undefined : { flex: 1 }}>
        <div className="u-name" style={forPrint ? undefined : { fontWeight: 600 }}>{it.name}</div>
        {it.product_type === 'bulk' ? (
          <div className="u-volume" style={forPrint ? undefined : { fontSize: 18, fontWeight: 700 }}>
            {qtyText(it)}
          </div>
        ) : (
          <div>{qtyText(it)}</div>
        )}
        <div className="u-gs1" style={forPrint ? undefined : { fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all', color: '#555' }}>
          {it.raw_code ? rawToGs1(it.raw_code) || it.raw_code : it.marking_code}
        </div>
        <div className="u-order" style={forPrint ? undefined : { marginTop: 4, color: '#555' }}>
          Заказ № {it.order_number || it.order_id}
        </div>
        {!forPrint && (
          <div style={{ marginTop: 8 }}>
            <Checkbox
              checked={!!printed[it.marking_code]}
              onChange={(e) => togglePrinted(it.marking_code, e.target.checked)}
            >
              Распечатано
            </Checkbox>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={720}
      style={{ top: 20 }}
      title={`Печать марок к продаже в 1С (${printable.length} шт.)`}
      footer={
        <Space>
          <Button onClick={onClose}>Отмена</Button>
          <Button
            type="primary"
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            disabled={printable.length === 0}
          >
            Печать
          </Button>
        </Space>
      }
    >
      {noTail.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`Без криптохвоста — пересканировать с бутылки (${noTail.length} шт.)`}
          description={noTail.map((i) => i.name).join('; ')}
        />
      )}
      {printable.length === 0 ? (
        <Alert type="info" showIcon message="Нет марок с полным кодом для печати" />
      ) : (
        printable.map((it) => renderItem(it, false))
      )}

      {/* Скрытая область для печати */}
      <div style={{ display: 'none' }}>
        <div ref={printAreaRef}>
          <div className="u-header">Продажи без оприходования — марки к продаже в 1С</div>
          <div className="u-subheader">Дата печати: {today}</div>
          {printable.map((it) => renderItem(it, true))}
          {noTail.length > 0 && (
            <>
              <div className="u-retake-title">Марки без криптохвоста — пересканировать с бутылки:</div>
              {noTail.map((it) => (
                <div className="u-retake-item" key={it.id}>
                  {it.name} — {qtyText(it)} — заказ № {it.order_number || it.order_id} — {it.marking_code}
                </div>
              ))}
            </>
          )}
          <div className="u-footer">
            1С: продажа → сканировать марку с этого листа. После продажи закройте строки на странице «Без оприходования».
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default UnreceivedPrintPreview;
